import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, FileText, Clock, Search, FolderIcon, FolderOpen,
  FolderPlus, MoreVertical, X, ChevronDown, ChevronRight, Folder, Upload
} from 'lucide-react';
import type { FormListItem, Folder as FolderType, FormDocument } from '@nanoapp/types';
import { formsApi, foldersApi, customersApi, type CustomerDTO } from '../lib/api';
import { AssignCustomerModal } from '../components/AssignCustomerModal';
import {
  toFormListItem,
  toFormDocument,
  toFolder,
  formDocumentToPayload,
  createBlankFormDocument,
} from '../utils/formConverters';
import { loadAppSettings } from '../utils/appSettings';
import { readFormJson } from '../utils/exportHtml';
import FormCard from '../components/dashboard/FormCard';

// ─── 2026-05-18: helpers locais inline (substituem formStorage.ts) ───
// Pequenos wrappers que combinam formsApi/foldersApi + converters. Cada
// um chama API direto e converte pra tipo interno.

async function listFormsLocal(): Promise<FormListItem[]> {
  const page = await formsApi.list({ limit: 200 });
  return page.items.map(toFormListItem);
}

async function listFoldersLocal(): Promise<FolderType[]> {
  const page = await foldersApi.list();
  return page.items.map(toFolder);
}

async function saveFormLocal(doc: FormDocument): Promise<FormDocument> {
  const payload = formDocumentToPayload(doc);
  try {
    const r = await formsApi.update(doc.id, payload);
    return toFormDocument(r.form);
  } catch (e) {
    if (e && typeof e === 'object' && 'status' in e && (e as { status: number }).status === 404) {
      const r = await formsApi.create(payload);
      return toFormDocument(r.form);
    }
    throw e;
  }
}

async function deleteFormLocal(id: string): Promise<void> {
  await formsApi.remove(id);
}

async function duplicateFormLocal(id: string): Promise<FormDocument | null> {
  try {
    const r = await formsApi.get(id);
    const doc = toFormDocument(r.form);
    const copy: FormDocument = {
      ...doc,
      // novo id é gerado pelo backend no create (não passamos id no POST)
      settings: { ...doc.settings, title: doc.settings.title + ' (cópia)' },
    };
    const payload = formDocumentToPayload(copy);
    const created = await formsApi.create(payload);
    return toFormDocument(created.form);
  } catch {
    return null;
  }
}

async function saveFolderLocal(folder: FolderType): Promise<FolderType> {
  try {
    const r = await foldersApi.update(folder.id, {
      title: folder.title,
      description: folder.description,
      color: folder.color,
      active: folder.active,
    });
    return toFolder(r.folder);
  } catch (e) {
    if (e && typeof e === 'object' && 'status' in e && (e as { status: number }).status === 404) {
      const r = await foldersApi.create({
        title: folder.title,
        description: folder.description,
        color: folder.color,
        active: folder.active,
      });
      return toFolder(r.folder);
    }
    throw e;
  }
}

async function deleteFolderLocal(id: string): Promise<void> {
  await foldersApi.remove(id);
}

async function createFolderLocal(title: string): Promise<FolderType> {
  const r = await foldersApi.create({ title, color: '#6366f1', active: true });
  return toFolder(r.folder);
}

async function moveFormToFolderLocal(formId: string, folderId: string | null): Promise<void> {
  await formsApi.update(formId, { folder_id: folderId });
}
// AppSettingsModal agora é acessado via Layout sidebar — removido daqui.

export default function FormsPage() {
  const [forms, setForms] = useState<FormListItem[]>([]);
  const [folders, setFolders] = useState<FolderType[]>([]);
  // 2026-05-18: customers pra filter dropdown
  const [customers, setCustomers] = useState<CustomerDTO[]>([]);
  const [filterCustomerId, setFilterCustomerId] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderTitle, setNewFolderTitle] = useState('');
  const [folderMenuId, setFolderMenuId] = useState<string | null>(null);
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [editingFolderTitle, setEditingFolderTitle] = useState('');
  const [movingFormId, setMovingFormId] = useState<string | null>(null);
  // 2026-05-19: AssignCustomerModal pra atribuir customer direto da lista
  const [assigningCustomerForForm, setAssigningCustomerForForm] = useState<FormListItem | null>(null);
  const [collapsedFolders, setCollapsedFolders] = useState<Set<string>>(new Set());
  const [appLogo, setAppLogo] = useState('');
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState('');
  const importInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [formsList, foldersList, custs] = await Promise.all([
        listFormsLocal(),
        listFoldersLocal(),
        customersApi.list().then((r) => r.items).catch(() => [] as CustomerDTO[]),
      ]);
      if (cancelled) return;
      setForms(formsList);
      setFolders(foldersList);
      setCustomers(custs);
      const s = loadAppSettings();
      setAppLogo(s.logo);
    })().catch(() => {});
    return () => { cancelled = true; };
  }, []);

  async function refresh() {
    const [formsList, foldersList] = await Promise.all([listFormsLocal(), listFoldersLocal()]);
    setForms(formsList);
    setFolders(foldersList);
  }

  async function handleNew() {
    const blank = createBlankFormDocument();
    const saved = await saveFormLocal(blank);
    navigate(`/builder/${saved.id}`);
  }

  async function handleImportFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    try {
      const doc = await readFormJson(file);
      await saveFormLocal(doc);
      await refresh();
      setImportSuccess(`"${doc.settings.title || 'Formulário'}" importado com sucesso!`);
      setTimeout(() => setImportSuccess(''), 3500);
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Erro ao importar.');
      setTimeout(() => setImportError(''), 4000);
    }
  }

  function handleEdit(id: string) { navigate(`/builder/${id}`); }
  async function handleDelete(id: string) { await deleteFormLocal(id); await refresh(); }
  async function handleDuplicate(id: string) { await duplicateFormLocal(id); await refresh(); }

  const handleCreateFolder = async () => {
    if (!newFolderTitle.trim()) return;
    await createFolderLocal(newFolderTitle.trim());
    await refresh();
    setNewFolderTitle('');
    setCreatingFolder(false);
  };

  const handleDeleteFolder = async (id: string) => {
    await deleteFolderLocal(id);
    // Backend já desvincula forms na DELETE, mas mantemos UI consistente.
    await refresh();
    setFolderMenuId(null);
  };

  const handleToggleFolderActive = async (folder: FolderType) => {
    await saveFolderLocal({ ...folder, active: !folder.active });
    await refresh();
    setFolderMenuId(null);
  };

  const handleRenameFolder = async (id: string) => {
    if (!editingFolderTitle.trim()) return;
    const folder = folders.find(f => f.id === id);
    if (!folder) return;
    await saveFolderLocal({ ...folder, title: editingFolderTitle.trim() });
    await refresh();
    setEditingFolderId(null);
  };

  const handleMoveForm = async (formId: string, folderId: string | null) => {
    await moveFormToFolderLocal(formId, folderId);
    await refresh();
    setMovingFormId(null);
  };

  const toggleFolderCollapse = (id: string) => {
    setCollapsedFolders(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const q = searchQuery.toLowerCase();
  const matchSearch = (f: FormListItem) => !q || f.title.toLowerCase().includes(q);
  // 2026-05-18: filter por customer (tag visual). Direct: f.customerId match;
  // Por enquanto não resolvemos herança via app (precisaria fetch de apps),
  // então 'all'/'none'/customerId apenas olha f.customerId.
  const matchCustomer = (f: FormListItem) => {
    if (filterCustomerId === 'all') return true;
    if (filterCustomerId === 'none') return !f.customerId;
    return f.customerId === filterCustomerId;
  };
  const matchForm = (f: FormListItem) => matchSearch(f) && matchCustomer(f);
  const matchFolder = (f: FolderType) => !q || f.title.toLowerCase().includes(q);

  const formsWithFolder = forms.filter(f => f.folderId && matchForm(f));
  const formsWithoutFolder = forms.filter(f => !f.folderId && matchForm(f));
  const visibleFolders = folders.filter(f => f.active && (matchFolder(f) || formsWithFolder.some(form => form.folderId === f.id)));

  return (
    <div className="min-h-screen" style={{ background: 'var(--app-bg, #f1f5f9)' }}>
      {/* Header — sub-header da página Forms (Layout já tem logo/settings na sidebar) */}
      <header className="sticky top-0 z-10 border-b" style={{ background: 'var(--app-surface, #fff)', borderColor: 'var(--app-border, #e5e7eb)' }}>
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-base font-bold leading-none" style={{ color: 'var(--app-text, #1f2937)' }}>Forms</h1>
              <p className="text-xs" style={{ color: 'var(--app-text-muted, #6b7280)' }}>Construtor de Formulários</p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-1 max-w-sm">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--app-text-muted, #9ca3af)' }} />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Buscar formulários e pastas..."
                className="w-full pl-9 pr-8 py-2 border rounded-lg text-sm focus:outline-none transition-colors"
                style={{ borderColor: 'var(--app-border, #e5e7eb)', background: 'var(--app-surface-2, #f8fafc)', color: 'var(--app-text, #1f2937)' }}
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2" style={{ color: 'var(--app-text-muted, #9ca3af)' }}>
                  <X size={13} />
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* 2026-05-18: filter por cliente */}
            {customers.length > 0 && (
              <select
                value={filterCustomerId}
                onChange={(e) => setFilterCustomerId(e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm bg-white focus:outline-none"
                style={{ borderColor: 'var(--app-border, #e5e7eb)' }}
                title="Filtrar formulários por cliente"
              >
                <option value="all">Todos os clientes</option>
                <option value="none">— Sem cliente —</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.icon ?? '👤'} {c.name}</option>
                ))}
              </select>
            )}
            <button
              onClick={() => setCreatingFolder(true)}
              className="flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm font-medium transition-colors"
              style={{ borderColor: 'var(--app-border, #e5e7eb)', color: 'var(--app-text-muted, #6b7280)' }}
            >
              <FolderPlus size={15} /> Pasta
            </button>
            <button
              onClick={() => importInputRef.current?.click()}
              className="flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm font-medium transition-colors hover:opacity-80"
              title="Importar formulário a partir de um arquivo .formcraft.json"
              style={{ borderColor: 'var(--app-border, #e5e7eb)', color: 'var(--app-text-muted, #6b7280)' }}
            >
              <Upload size={15} /> Importar
            </button>
            <input
              ref={importInputRef}
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={handleImportFile}
            />
            <button
              onClick={handleNew}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors hover:opacity-90"
              style={{ background: 'var(--app-accent, #6366f1)' }}
            >
              <Plus size={15} /> Novo Formulário
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6">
        {/* Stats */}
        <div className="flex items-center gap-6 mb-6">
          <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--app-text-muted, #6b7280)' }}>
            <FileText size={15} style={{ color: 'var(--app-accent, #6366f1)' }} />
            <span><strong style={{ color: 'var(--app-text, #1f2937)' }}>{forms.length}</strong> formulário{forms.length !== 1 ? 's' : ''}</span>
          </div>
          {folders.filter(f => f.active).length > 0 && (
            <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--app-text-muted, #6b7280)' }}>
              <Folder size={15} style={{ color: 'var(--app-accent, #6366f1)' }} />
              <span><strong style={{ color: 'var(--app-text, #1f2937)' }}>{folders.filter(f => f.active).length}</strong> pasta{folders.filter(f => f.active).length !== 1 ? 's' : ''}</span>
            </div>
          )}
          {forms.length > 0 && (
            <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--app-text-muted, #6b7280)' }}>
              <Clock size={15} style={{ color: 'var(--app-accent, #6366f1)' }} />
              <span>Atualizado recentemente</span>
            </div>
          )}
        </div>

        {forms.length === 0 && folders.length === 0 ? (
          <EmptyState onNew={handleNew} />
        ) : (
          <div className="space-y-8">
            {/* Pastas como seções */}
            {visibleFolders.map(folder => {
              const folderForms = forms.filter(f => f.folderId === folder.id && matchForm(f));
              const isCollapsed = collapsedFolders.has(folder.id);
              return (
                <section key={folder.id}>
                  {/* Cabeçalho da pasta */}
                  <div className="flex items-center gap-3 mb-3 group">
                    <button
                      onClick={() => toggleFolderCollapse(folder.id)}
                      className="flex items-center gap-2 flex-1 text-left"
                    >
                      <div className="flex items-center justify-center w-6">
                        {isCollapsed
                          ? <ChevronRight size={16} style={{ color: 'var(--app-text-muted, #9ca3af)' }} />
                          : <ChevronDown size={16} style={{ color: 'var(--app-text-muted, #9ca3af)' }} />
                        }
                      </div>
                      <FolderOpen size={18} style={{ color: folder.color }} />
                      {editingFolderId === folder.id ? (
                        <input
                          autoFocus
                          type="text"
                          value={editingFolderTitle}
                          onChange={e => setEditingFolderTitle(e.target.value)}
                          onBlur={() => handleRenameFolder(folder.id)}
                          onKeyDown={e => { if (e.key === 'Enter') handleRenameFolder(folder.id); if (e.key === 'Escape') setEditingFolderId(null); }}
                          className="border-b-2 outline-none text-sm font-semibold bg-transparent"
                          style={{ borderColor: 'var(--app-accent, #6366f1)', color: 'var(--app-text, #1f2937)' }}
                          onClick={e => e.stopPropagation()}
                        />
                      ) : (
                        <span className="text-sm font-semibold" style={{ color: 'var(--app-text, #1f2937)' }}>{folder.title}</span>
                      )}
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--app-accent-20, #6366f133)', color: 'var(--app-accent, #6366f1)' }}>
                        {folderForms.length}
                      </span>
                    </button>

                    {/* Menu da pasta */}
                    <div className="relative opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={e => { e.stopPropagation(); setFolderMenuId(folderMenuId === folder.id ? null : folder.id); }}
                        className="w-7 h-7 rounded-lg flex items-center justify-center border transition-colors"
                        style={{ borderColor: 'var(--app-border, #e5e7eb)', color: 'var(--app-text-muted, #6b7280)' }}
                      >
                        <MoreVertical size={13} />
                      </button>
                      {folderMenuId === folder.id && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setFolderMenuId(null)} />
                          <div className="absolute right-0 top-8 rounded-xl shadow-lg z-20 min-w-36 py-1 border"
                            style={{ background: 'var(--app-surface, #fff)', borderColor: 'var(--app-border, #e5e7eb)' }}>
                            <button onClick={() => { setEditingFolderId(folder.id); setEditingFolderTitle(folder.title); setFolderMenuId(null); }}
                              className="w-full text-left px-3 py-1.5 text-sm hover:opacity-70" style={{ color: 'var(--app-text, #1f2937)' }}>Renomear</button>
                            <button onClick={() => handleToggleFolderActive(folder)}
                              className="w-full text-left px-3 py-1.5 text-sm hover:opacity-70" style={{ color: 'var(--app-text, #1f2937)' }}>
                              {folder.active ? 'Inativar' : 'Ativar'}
                            </button>
                            <button onClick={() => handleDeleteFolder(folder.id)}
                              className="w-full text-left px-3 py-1.5 text-sm text-red-500 hover:opacity-70">Excluir pasta</button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Conteúdo da pasta */}
                  {!isCollapsed && (
                    <div className="pl-8">
                      {folderForms.length === 0 ? (
                        <div className="border-2 border-dashed rounded-xl p-6 text-center text-sm"
                          style={{ borderColor: 'var(--app-border, #e5e7eb)', color: 'var(--app-text-muted, #9ca3af)' }}>
                          Nenhum formulário nesta pasta. Use "Mover para pasta" no menu do formulário.
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          {folderForms.map(form => (
                            <FormCard key={form.id} form={form}
                              customer={form.customerId ? customers.find(c => c.id === form.customerId) ?? null : null}
                              onEdit={() => handleEdit(form.id)}
                              onDelete={() => handleDelete(form.id)}
                              onDuplicate={() => handleDuplicate(form.id)}
                              onMoveToFolder={() => setMovingFormId(form.id)}
                              onAssignCustomer={() => setAssigningCustomerForForm(form)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </section>
              );
            })}

            {/* Seção: Sem pasta / Todos os formulários */}
            {(formsWithoutFolder.length > 0 || visibleFolders.length === 0) && (
              <section>
                {visibleFolders.length > 0 && (
                  <div className="flex items-center gap-2 mb-3">
                    <FolderIcon size={18} style={{ color: 'var(--app-text-muted, #9ca3af)' }} />
                    <span className="text-sm font-semibold" style={{ color: 'var(--app-text, #1f2937)' }}>Sem pasta</span>
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--app-border, #e5e7eb)', color: 'var(--app-text-muted, #6b7280)' }}>
                      {formsWithoutFolder.length}
                    </span>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Card "Novo Formulário" */}
                  <button
                    onClick={handleNew}
                    className="group border-2 border-dashed rounded-xl p-6 flex flex-col items-center justify-center gap-3 transition-all min-h-[140px] hover:border-opacity-100"
                    style={{ borderColor: 'var(--app-border, #e5e7eb)', color: 'var(--app-text-muted, #9ca3af)' }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--app-accent, #6366f1)')}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--app-border, #e5e7eb)')}
                  >
                    <div className="w-10 h-10 rounded-full flex items-center justify-center transition-colors" style={{ background: 'var(--app-border, #f3f4f6)' }}>
                      <Plus size={20} />
                    </div>
                    <span className="text-sm font-medium">Novo Formulário</span>
                  </button>

                  {formsWithoutFolder.map(form => (
                    <FormCard key={form.id} form={form}
                      customer={form.customerId ? customers.find(c => c.id === form.customerId) ?? null : null}
                      onEdit={() => handleEdit(form.id)}
                      onDelete={() => handleDelete(form.id)}
                      onDuplicate={() => handleDuplicate(form.id)}
                      onMoveToFolder={() => setMovingFormId(form.id)}
                      onAssignCustomer={() => setAssigningCustomerForForm(form)}
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </main>

      {/* Modal: Criar pasta */}
      {creatingFolder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.4)' }} onClick={() => setCreatingFolder(false)}>
          <div className="rounded-2xl shadow-xl p-6 w-full max-w-sm" style={{ background: 'var(--app-surface, #fff)', color: 'var(--app-text, #1f2937)' }} onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4">Nova Pasta</h3>
            <input autoFocus type="text" value={newFolderTitle}
              onChange={e => setNewFolderTitle(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleCreateFolder(); if (e.key === 'Escape') setCreatingFolder(false); }}
              placeholder="Nome da pasta..."
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none mb-4"
              style={{ borderColor: 'var(--app-border, #e5e7eb)', background: 'var(--app-surface-2, #f8fafc)', color: 'var(--app-text, #1f2937)' }}
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setCreatingFolder(false)} className="px-4 py-2 text-sm" style={{ color: 'var(--app-text-muted, #6b7280)' }}>Cancelar</button>
              <button onClick={handleCreateFolder} className="px-4 py-2 text-sm text-white rounded-lg font-medium hover:opacity-90" style={{ background: 'var(--app-accent, #6366f1)' }}>Criar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Mover form para pasta */}
      {movingFormId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.4)' }} onClick={() => setMovingFormId(null)}>
          <div className="rounded-2xl shadow-xl p-6 w-full max-w-sm" style={{ background: 'var(--app-surface, #fff)', color: 'var(--app-text, #1f2937)' }} onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4">Mover para pasta</h3>
            <div className="flex flex-col gap-1">
              <button
                onClick={() => { void handleMoveForm(movingFormId, null); }}
                className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-colors hover:opacity-80"
                style={{ color: 'var(--app-text, #1f2937)' }}
              >
                <FolderOpen size={16} style={{ color: 'var(--app-text-muted, #9ca3af)' }} /> Sem pasta
              </button>
              {folders.filter(f => f.active).map(folder => (
                <button key={folder.id}
                  onClick={() => { void handleMoveForm(movingFormId, folder.id); }}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-colors hover:opacity-80"
                  style={{ color: 'var(--app-text, #1f2937)' }}
                >
                  <FolderIcon size={16} style={{ color: folder.color }} /> {folder.title}
                </button>
              ))}
            </div>
            <button onClick={() => setMovingFormId(null)} className="mt-3 w-full text-sm pt-3 border-t" style={{ color: 'var(--app-text-muted, #6b7280)', borderColor: 'var(--app-border, #e5e7eb)' }}>Cancelar</button>
          </div>
        </div>
      )}

      {/* Toast: importação */}
      {(importSuccess || importError) && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-xl shadow-xl text-sm font-medium flex items-center gap-2 transition-all"
          style={{
            background: importSuccess ? '#064e3b' : '#450a0a',
            color: importSuccess ? '#a7f3d0' : '#fca5a5',
            border: `1px solid ${importSuccess ? '#059669' : '#dc2626'}`,
          }}
        >
          {importSuccess ? '✓ ' + importSuccess : '✕ ' + importError}
        </div>
      )}

      {/* 2026-05-19: Modal pra atribuir cliente direto da lista */}
      {assigningCustomerForForm && (
        <AssignCustomerModal
          resourceLabel={assigningCustomerForForm.title}
          resourceKind="form"
          currentCustomerId={assigningCustomerForForm.customerId ?? null}
          onClose={() => setAssigningCustomerForForm(null)}
          onSave={async (customerId) => {
            await formsApi.update(assigningCustomerForForm.id, { customer_id: customerId });
            await refresh();
          }}
        />
      )}
    </div>
  );
}

function EmptyState({ onNew }: { onNew: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-5" style={{ background: 'var(--app-accent-10, #eef2ff)' }}>
        <FileText size={36} style={{ color: 'var(--app-accent, #6366f1)' }} />
      </div>
      <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--app-text, #1f2937)' }}>Nenhum formulário ainda</h2>
      <p className="mb-6 max-w-sm" style={{ color: 'var(--app-text-muted, #6b7280)' }}>
        Crie seu primeiro formulário e comece a coletar respostas de forma profissional.
      </p>
      <button
        onClick={onNew}
        className="flex items-center gap-2 text-white px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity"
        style={{ background: 'var(--app-accent, #6366f1)' }}
      >
        <Plus size={18} /> Criar Formulário
      </button>
    </div>
  );
}
